/**
 * Affichage de l'avancement d'une installation.
 *
 * Module **pur**, comme celui des mises à jour, et pour la même raison : un indicateur qui invente
 * une étape serait pire que pas d'indicateur. Ce qui est affiché dérive de l'état persisté par la
 * machine à états du serveur, jamais d'une supposition sur ce qui « doit » se passer ensuite.
 *
 * L'installation dure de longues minutes — Docker à installer, trois images à télécharger, une base
 * à créer — et c'est justement pendant ce temps que la personne a besoin de savoir où l'on en est.
 * Un long moment sans nouvelle sur un serveur qu'on vient de confier à un outil est la meilleure
 * façon de faire douter de l'outil.
 */

/** Les états que le serveur peut rendre, repris de l'enum partagé. */
export type StatutInstallation =
  | 'en_file'
  | 'preparation'
  | 'docker'
  | 'bordure'
  | 'pile'
  | 'ghost'
  | 'enregistrement'
  | 'dns'
  | 'terminee'
  | 'echouee'
  | 'interrompue';

export type AvancementÉtape = 'faite' | 'courante' | 'attente' | 'interrompue';

export interface ÉtapeAffichée {
  clé: StatutInstallation;
  libellé: string;
  /** Ce qui se passe, dit à la première personne du pluriel : la personne attend avec nous. */
  détail: string;
  état: AvancementÉtape;
}

/**
 * Les étapes montrées, dans l'ordre.
 *
 * `en_file` n'y figure pas : elle ne dure qu'un instant, et l'afficher ferait clignoter une étape
 * que personne n'a le temps de lire.
 */
export const ÉTAPES_INSTALLATION: readonly {
  clé: StatutInstallation;
  libellé: string;
  détail: string;
}[] = [
  {
    clé: 'preparation',
    libellé: 'Vérifications',
    détail: 'Système, privilèges, place disque, mémoire, ports libres.',
  },
  {
    clé: 'docker',
    libellé: 'Docker',
    détail: 'Installation depuis le dépôt officiel, clé de signature vérifiée.',
  },
  {
    clé: 'bordure',
    libellé: 'Proxy TLS',
    détail: 'Le proxy de l’hôte, qui obtiendra le certificat du site.',
  },
  {
    clé: 'pile',
    libellé: 'Ghost et sa base',
    détail: 'Téléchargement des images, premier démarrage, création du schéma.',
  },
  {
    clé: 'ghost',
    libellé: 'Compte et clé d’API',
    détail: 'Le compte propriétaire, et la clé qui permet à Vigie de superviser.',
  },
  {
    clé: 'enregistrement',
    libellé: 'Enregistrement',
    détail: 'Le site rejoint la flotte : sauvegardes et mises à jour comprises.',
  },
  {
    clé: 'dns',
    libellé: 'Mise en ligne',
    détail:
      'Les enregistrements DNS à poser et les ports 80 et 443 à ouvrir, puis le certificat ' +
      's’obtient tout seul.',
  },
];

const ORDRE: readonly StatutInstallation[] = ÉTAPES_INSTALLATION.map((étape) => étape.clé);

/**
 * L'avancement, étape par étape.
 *
 * `echouee` et `interrompue` n'ont **pas de position** sur cette échelle : l'installation s'est
 * arrêtée quelque part, et prétendre savoir où serait inventer. Le journal, lui, le dit exactement,
 * et c'est là qu'on renvoie.
 */
export function avancement(statut: StatutInstallation): ÉtapeAffichée[] {
  if (statut === 'echouee' || statut === 'interrompue') {
    return ÉTAPES_INSTALLATION.map((étape) => ({ ...étape, état: 'interrompue' as const }));
  }
  if (statut === 'terminee') {
    return ÉTAPES_INSTALLATION.map((étape) => ({ ...étape, état: 'faite' as const }));
  }
  // `en_file` : rien n'a commencé, tout est en attente.
  const index = statut === 'en_file' ? -1 : ORDRE.indexOf(statut);
  return ÉTAPES_INSTALLATION.map((étape, position) => ({
    ...étape,
    état: position < index ? 'faite' : position === index ? 'courante' : 'attente',
  }));
}

/** L'installation travaille-t-elle en ce moment ? C'est ce qui décide de continuer à interroger. */
export function estActive(statut: StatutInstallation): boolean {
  return [
    'en_file',
    'preparation',
    'docker',
    'bordure',
    'pile',
    'ghost',
    'enregistrement',
  ].includes(statut);
}

/** Attend-elle un geste chez le bureau d'enregistrement ? */
export function attendDns(statut: StatutInstallation): boolean {
  return statut === 'dns';
}

export function estReprenable(statut: StatutInstallation): boolean {
  return statut === 'echouee' || statut === 'interrompue';
}

/** Faut-il continuer à interroger le serveur ? */
export function doitSonder(statut: StatutInstallation): boolean {
  return estActive(statut) || attendDns(statut);
}

/**
 * Ce que dit le dernier contrôle DNS, en une phrase actionnable.
 *
 * Chaque verdict appelle un geste différent, et les confondre ferait chercher au mauvais endroit :
 * ajouter un enregistrement, en corriger un, en compléter un, ou simplement attendre.
 */
export function libelléDns(verdict: string | undefined): string {
  switch (verdict) {
    case 'correct':
      return 'Le nom pointe vers ce serveur.';
    case 'partiel':
      return 'Une famille d’adresses manque : le site sera injoignable depuis une partie des réseaux.';
    case 'autre_adresse':
      return 'Le nom pointe ailleurs qu’ici : l’enregistrement existe, mais il désigne un autre serveur.';
    case 'absent':
      return 'Aucun enregistrement trouvé. Il peut aussi être posé mais pas encore propagé.';
    default:
      return 'Pas encore vérifié.';
  }
}
